// components/AwsPricingEstimates.jsx — simpan / muat / hapus estimate.
//
// KENAPA KOMPONEN TERPISAH. Keranjang di AwsPricingCalculator murni state
// browser; tanpa berkas ini seluruh isinya hilang saat tab di-reload. Endpoint
// CRUD-nya sudah ada dan terverifikasi sejak Tahap 2 — yang belum ada hanya
// jalur UI-nya.
//
// SNAPSHOT, BUKAN REFERENSI. Baris estimate menyimpan harga yang berlaku saat
// disimpan (`unit_price_snapshot`, `upfront_snapshot`), bukan hanya `sku` atau
// `term_id`. Alasannya mekanis: sync mingguan menjalankan DELETE lalu insert
// ulang, dan `aws_price_term.id` adalah AUTOINCREMENT — jadi `term_id` yang
// tersimpan akan menunjuk baris LAIN setelah sync. Salah, bukan hilang, dan itu
// jauh lebih buruk karena tidak terlihat.
//
// Saat estimate dibuka, backend mengembalikan snapshot SEKALIGUS harga terkini
// hasil re-resolve lewat (region, usage_type, operation). Selisihnya ditandai
// `drift` dan ditampilkan — pengguna melihat "harga saat disimpan" berdampingan
// dengan "harga sekarang", bukan menemukan angkanya berubah sendiri.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import {
  Save, FolderOpen, Trash2, Loader2, AlertTriangle, X, TrendingUp, Clock,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// SERIALISASI
//
// Fungsi murni, diekspor supaya bisa diuji tanpa merender apa pun. Perjalanan
// keranjang → estimate → keranjang harus bolak-balik tanpa kehilangan apa pun;
// satu field yang terlewat baru terasa saat estimate dibuka minggu depan.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Baris keranjang → payload baris estimate.
 *
 * Harga snapshot disimpan PER SATUAN, bukan dikali qty. Backend membandingkan
 * snapshot dengan harga katalog yang juga per satuan; mengalikannya di sini akan
 * membuat setiap estimate ber-qty > 1 dilaporkan drift padahal harganya sama.
 */
// Jenis baris yang harganya berbasis MATRIKS komitmen (RI/SP) dan ditagih
// per jam, bukan per satuan volume. Sebelumnya kode ini hanya mengenal 'ec2',
// dan itu bukan kekurangan kosmetik: baris RDS/OpenSearch tersimpan seolah
// baris usage — hours_per_month hilang, unit_price_snapshot jadi 0, params
// kehilangan instance_type/db_engine — sehingga saat dibuka kembali baris itu
// dihitung sebagai usage bervolume 0 dan menjadi $0,00 TANPA pesan galat.
// Terbukti dengan menjalankan ulang serialisasinya, bukan dari gejala.
const COMPUTE_KINDS = ['ec2', 'rds', 'opensearch'];
const isComputeKind = (k) => COMPUTE_KINDS.includes(k);

const SERVICE_OF_KIND = { ec2: 'AmazonEC2', rds: 'AmazonRDS', opensearch: 'AmazonES' };

/**
 * Baris keranjang → payload baris estimate.
 *
 * Harga snapshot disimpan PER SATUAN, bukan dikali qty. Backend membandingkan
 * snapshot dengan harga katalog yang juga per satuan; mengalikannya di sini akan
 * membuat setiap estimate ber-qty > 1 dilaporkan drift padahal harganya sama.
 */
export function toEstimateLine(line, index, matrixCell) {
  const kind = isComputeKind(line.kind) ? line.kind : 'usage';
  const compute = kind !== 'usage';
  const choice = line.choice || { kind: 'od' };
  const termType = choice.kind === 'od' ? 'ondemand' : choice.kind;

  const unitPrice = compute
    ? (choice.kind === 'od'
        ? Number(line.rates?.onDemand?.hourly) || 0
        : Number(matrixCell?.hourly) || 0)
    : Number(line.rates?.tiers?.[0]?.price_per_unit) || 0;

  // `params` adalah satu-satunya tempat pilihan UI bertahan. Endpoint
  // /calculator/rates me-resolve baris compute dari pilihan itu (bukan dari
  // usage_type), jadi field yang terlewat di sini membuat estimate bisa dibaca
  // tetapi tidak bisa dihitung ulang.
  const params = kind === 'ec2' ? {
    kind: 'ec2',
    instance_type: line.instance_type,
    os: line.os,
    tenancy: line.tenancy,
    pre_installed_sw: line.pre_installed_sw || 'NA',
  } : kind === 'rds' ? {
    kind: 'rds',
    instance_type: line.instance_type,
    db_engine: line.db_engine,
    db_edition: line.db_edition || '',
    deployment_option: line.deployment_option,
    license_model: line.license_model || '',
  } : kind === 'opensearch' ? {
    kind: 'opensearch',
    instance_type: line.instance_type,
  } : {
    // usage_key disimpan supaya baris impor tetap bisa ditulis ke lembar
    // Re-import setelah estimate dibuka kembali. Bisa null: baris usage yang
    // dirakit manual tidak pernah punya nilai ini, dan estimate yang disimpan
    // SEBELUM perubahan ini juga tidak. readParams() toleran terhadap field
    // yang hilang, jadi baris lama tetap terbaca — tanpa usage_key, dan itu
    // jujur terhadap datanya.
    kind: 'usage',
    usage_key: line.usage_key || null,
  };

  return {
    sort_order: index,
    service_code: SERVICE_OF_KIND[kind] || line.service_code,
    region: line.region,
    label: line.label,
    sku: line.rates?.resolved?.sku || null,
    term_type: termType,
    usage_type: line.usage_type || null,
    operation: line.operation || null,
    offering_class: choice.kind === 'ri' ? choice.offering : null,
    plan_type: choice.kind === 'sp' ? choice.plan_type : null,
    lease_length: choice.kind === 'od' ? null : choice.lease,
    purchase_option: choice.kind === 'od' ? null : choice.payment,
    qty: Number(line.qty) || 1,
    hours_per_month: compute ? Number(line.hoursPerMonth) || null : null,
    volume_gb: compute ? null : Number(line.volumeGb) || 0,
    unit_price_snapshot: unitPrice,
    upfront_snapshot: choice.kind === 'od' ? 0 : Number(matrixCell?.upfront) || 0,
    params,
  };
}

/**
 * Bentuk `params_json` yang tersimpan, dengan cadangan untuk baris lama.
 *
 * Baris yang disimpan SEBELUM perbaikan ini menuliskan {kind:'usage'} untuk
 * RDS/OpenSearch dan tidak memuat instance_type — datanya memang tidak ada,
 * jadi tidak bisa dipulihkan. Baris seperti itu tetap dimuat sebagai usage
 * (perilaku lama) dan harus disimpan ulang oleh pengguna; menebak
 * instance_type dari label akan menghasilkan harga yang tampak benar padahal
 * dikarang.
 */
function readParams(l) {
  let p = {};
  try { p = l.params_json ? JSON.parse(l.params_json) : {}; } catch (_) { p = {}; }
  if (!p.kind) p.kind = l.service_code === 'AmazonEC2' && l.hours_per_month ? 'ec2' : 'usage';
  return p;
}

/** Baris estimate → spec untuk POST /api/aws/calculator/rates. */
export function toRateSpec(l, key) {
  const p = readParams(l);
  if (p.kind === 'ec2') {
    return {
      key, kind: 'ec2', region: l.region,
      instance_type: p.instance_type, os: p.os, tenancy: p.tenancy,
      pre_installed_sw: p.pre_installed_sw || 'NA',
    };
  }
  if (p.kind === 'rds') {
    return {
      key, kind: 'rds', region: l.region,
      instance_type: p.instance_type, db_engine: p.db_engine,
      db_edition: p.db_edition || '', deployment_option: p.deployment_option,
      license_model: p.license_model || '',
    };
  }
  if (p.kind === 'opensearch') {
    return { key, kind: 'opensearch', region: l.region, instance_type: p.instance_type };
  }
  // `operation` WAJIB dikirim. usage_type saja tidak unik dalam satu service:
  // AWSELB memakai satu usage_type untuk empat jenis load balancer, dan
  // NAT Gateway memakai satu untuk NatGateway maupun RegionalNatGateway.
  // Kebetulan tarifnya sering sama, jadi kelalaian ini menghasilkan SKU yang
  // salah tanpa selisih angka — kesalahan yang tidak bisa terlihat dari layar.
  return {
    key, kind: 'usage', region: l.region,
    service_code: l.service_code, usage_type: l.usage_type,
    operation: l.operation || '',
  };
}

/** Baris estimate + harga segar → baris keranjang. */
export function toCartLine(l, rates, id) {
  const p = readParams(l);
  const kind = isComputeKind(p.kind) ? p.kind : 'usage';
  const compute = kind !== 'usage';
  const choice = l.term_type === 'ondemand' || !l.term_type
    ? { kind: 'od' }
    : l.term_type === 'sp'
      ? { kind: 'sp', plan_type: l.plan_type, lease: l.lease_length, payment: l.purchase_option }
      : { kind: 'ri', offering: l.offering_class, lease: l.lease_length, payment: l.purchase_option };

  return {
    id,
    kind,
    region: l.region,
    label: l.label || l.usage_type || '—',
    // Pilihan EC2
    instance_type: p.instance_type, os: p.os, tenancy: p.tenancy,
    pre_installed_sw: p.pre_installed_sw,
    // Pilihan RDS — tanpa ini baris RDS bisa dihitung tetapi tidak bisa
    // disimpan ulang dengan benar, karena toEstimateLine membacanya dari sini.
    db_engine: p.db_engine, db_edition: p.db_edition,
    deployment_option: p.deployment_option, license_model: p.license_model,
    service_code: l.service_code,
    usage_type: l.usage_type,
    operation: l.operation,
    usage_key: p.usage_key || null,
    qty: Number(l.qty) || 1,
    hoursPerMonth: Number(l.hours_per_month) || 730,
    // volumeGb tidak berlaku untuk baris compute. Memberinya 0 tidak berbahaya
    // di sana, tetapi menyetel null menjaga bentuknya jujur bila kelak ada kode
    // yang membedakan "nol" dari "tidak berlaku".
    volumeGb: compute ? null : Number(l.volume_gb) || 0,
    choice,
    rates,
    // Dibawa dari server supaya panel bisa menandai baris yang harganya sudah
    // berubah sejak disimpan, tanpa menghitungnya ulang di sini.
    drift: l.drift || null,
    snapshot: {
      unit_price: Number(l.unit_price_snapshot) || 0,
      upfront: Number(l.upfront_snapshot) || 0,
      priced_at: l.priced_at || null,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────

export default function AwsPricingEstimates({
  lines, region, vatCountry, supportPlan, fxRate, findCell, onLoaded, onDirtyReset,
}) {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [currentName, setCurrentName] = useState('');

  const loadList = useCallback(async () => {
    try {
      const res = await api.get('/api/aws/estimates');
      setList(res.data.rows || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const buildPayload = (name) => ({
    name,
    default_region: region,
    display_currency: 'USD',
    vat_country: vatCountry,
    support_plan: supportPlan,
    fx_rate_snapshot: fxRate || null,
    lines: lines.map((l, i) => {
      // Sebelumnya hanya 'ec2'. Akibatnya baris RDS/OpenSearch berkomitmen
      // kehilangan upfront_snapshot dan harga satuannya — lalu menjadi $0,00
      // saat dibuka kembali.
      const cell = l.kind !== 'usage' && l.choice && l.choice.kind !== 'od'
        ? findCell(l.rates?.matrix, l.choice) : null;
      return toEstimateLine(l, i, cell);
    }),
  });

  const save = async (asNew) => {
    if (!lines.length) { setError(t('awsp.est.emptyCart')); return; }
    const name = asNew || !currentId
      ? window.prompt(t('awsp.est.namePrompt'), currentName || t('awsp.est.defaultName'))
      : currentName;
    if (!name) return;

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (asNew || !currentId) {
        const res = await api.post('/api/aws/estimates', buildPayload(name));
        setCurrentId(res.data.id);
        setCurrentName(name);
        setNotice(t('awsp.est.saved').replace('{n}', String(res.data.lines)));
      } else {
        const res = await api.put(`/api/aws/estimates/${currentId}`, buildPayload(name));
        setNotice(t('awsp.est.saved').replace('{n}', String(res.data.lines)));
      }
      if (onDirtyReset) onDirtyReset();
      await loadList();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const load = async (id) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const det = await api.get(`/api/aws/estimates/${id}`);
      const est = det.data.estimate;
      const rows = det.data.lines || [];

      if (!rows.length) {
        onLoaded({ lines: [], estimate: est });
        setCurrentId(id);
        setCurrentName(est.name);
        setNotice(t('awsp.est.loadedEmpty'));
        return;
      }

      // Harga diambil ULANG, bukan dipakai dari snapshot: keranjang perlu
      // seluruh matriks komitmen agar pengguna bisa mengganti pilihan setelah
      // memuat. Snapshot tetap dibawa untuk pembanding.
      const specs = rows.map((l, i) => toRateSpec(l, `e${i}`));
      const rateRes = await api.post('/api/aws/calculator/rates', { lines: specs });
      const byKey = {};
      (rateRes.data.lines || []).forEach(r => { byKey[r.key] = r; });

      const cart = rows.map((l, i) => toCartLine(l, byKey[`e${i}`] || null, `e${id}-${i}`));
      onLoaded({ lines: cart, estimate: est });
      setCurrentId(id);
      setCurrentName(est.name);
      setOpen(false);

      const drift = det.data.driftCount || 0;
      const dead = cart.filter(c => !c.rates || c.rates.error).length;
      setNotice(
        drift || dead
          ? t('awsp.est.loadedDrift').replace('{d}', String(drift)).replace('{x}', String(dead))
          : t('awsp.est.loaded')
      );
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(t('awsp.est.confirmDelete').replace('{name}', name))) return;
    setBusy(true);
    try {
      await api.delete(`/api/aws/estimates/${id}`);
      if (currentId === id) { setCurrentId(null); setCurrentName(''); }
      await loadList();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="px-3 py-2 flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-700">{t('awsp.est.title')}</p>
          <p className="text-xs text-gray-500 truncate">
            {currentId
              ? t('awsp.est.currentName').replace('{name}', currentName).replace('{id}', String(currentId))
              : t('awsp.est.unsaved')}
          </p>
        </div>

        <button
          onClick={() => save(false)}
          disabled={busy || !lines.length}
          className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {currentId ? t('awsp.est.save') : t('awsp.est.saveNew')}
        </button>

        {currentId && (
          <button
            onClick={() => save(true)}
            disabled={busy || !lines.length}
            className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-50 disabled:opacity-40"
          >
            {t('awsp.est.saveAs')}
          </button>
        )}

        <button
          onClick={() => setOpen(o => !o)}
          className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-50 inline-flex items-center gap-1.5"
        >
          <FolderOpen className="w-3.5 h-3.5" />
          {t('awsp.est.open')} <span className="text-gray-400">({list.length})</span>
        </button>
      </div>

      {error && (
        <div className="mx-3 mb-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {notice && (
        <div className="mx-3 mb-2 rounded border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-800 flex items-start gap-1.5">
          <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice(null)}><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {open && (
        <div className="border-t border-gray-100">
          {!list.length && (
            <p className="px-3 py-3 text-xs text-gray-500">{t('awsp.est.listEmpty')}</p>
          )}
          <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {list.map(e => (
              <li key={e.id} className="px-3 py-2 flex items-center gap-2 hover:bg-gray-50">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-800 truncate">{e.name}</p>
                  <p className="text-xs text-gray-500">
                    #{e.id} · {e.default_region} · {t('awsp.est.lineCount').replace('{n}', String(e.line_count))}
                    {e.updated_at ? ` · ${e.updated_at}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => load(e.id)}
                  disabled={busy}
                  className="px-2 py-1 rounded border border-gray-300 text-xs text-gray-700 hover:bg-white disabled:opacity-40"
                >
                  {t('awsp.est.loadBtn')}
                </button>
                <button
                  onClick={() => remove(e.id, e.name)}
                  disabled={busy}
                  className="p-1 rounded text-red-600 hover:bg-red-50 disabled:opacity-40"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="px-3 py-2 border-t border-gray-100 text-xs text-gray-400 flex items-start gap-1.5">
        <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        {t('awsp.est.snapshotNote')}
      </p>
    </div>
  );
}
